import React, { useState, useEffect } from "react";
import {
    Box, Paper, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Chip,
    CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, Tooltip, InputAdornment, TextField,
    Avatar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import axiosInstance from "./Axiosinstance";

// ── helpers ──────────────────────────────────────────────────────────────────

const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const avatarColor = (name = "") => {
    const colors = ["#1976d2", "#7b1fa2", "#0288d1", "#2e7d32", "#f57c00", "#c62828"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

// ── component ─────────────────────────────────────────────────────────────────

const AdminCareers = () => {
    const [applications, setApplications] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [viewItem, setViewItem] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    // ── fetch ──────────────────────────────────────────────────────────────────

    const fetchApplications = () => {
        setLoading(true);
        axiosInstance.get("/api/admin/careers")
            .then((res) => {
                const data = res.data.data || [];
                setApplications(data);
                setFiltered(data);
            })
            .catch(() => setError("Failed to load career applications."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchApplications(); }, []);

    // ── search ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        const q = search.toLowerCase();
        setFiltered(
            applications.filter((a) =>
                // (a.name || "").toLowerCase().includes(q) ||
                // (a.email || "").toLowerCase().includes(q) ||
                // (a.position || "").toLowerCase().includes(q) ||
                // (a.phone || "").toLowerCase().includes(q)
                (`${a.firstName || ""} ${a.lastName || ""}`)
                    .toLowerCase()
                    .includes(q) ||

                (a.email || "").toLowerCase().includes(q) ||

                (a.education || "").toLowerCase().includes(q) ||

                (a.phoneNumber || "").includes(q)
            )
        );
    }, [search, applications]);

    // ── delete ─────────────────────────────────────────────────────────────────

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/api/admin/careers/${deleteDialog.id}`);
            setSuccess("Application deleted.");
            setDeleteDialog({ open: false, id: null });
            fetchApplications();
        } catch {
            setError("Failed to delete application.");
        }
    };

    // ── render ─────────────────────────────────────────────────────────────────

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Career Applications</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filtered.length} application{filtered.length !== 1 ? "s" : ""} found
                    </Typography>
                </Box>
            </Box>

            {/* Alerts */}
            {success && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search by name, email, position, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" } }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}
            >
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: "rgba(46,125,50,0.04)" }}>
                            {["#", "Applicant", "Email", "Phone", "Education", "Applied On", "Actions"].map((h) => (
                                <TableCell
                                    key={h}
                                    sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary", py: 1.75 }}
                                >
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                                    No applications found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((item, idx) => (
                                <TableRow key={item.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                    {/* # */}
                                    <TableCell sx={{ color: "text.secondary", fontSize: "13px" }}>
                                        {idx + 1}
                                    </TableCell>

                                    {/* Applicant */}
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    width: 34, height: 34,
                                                    fontSize: "12px", fontWeight: 700,
                                                    background: avatarColor(
                                                        `${item.firstName || ""} ${item.lastName || ""}`
                                                    ),
                                                }}
                                            >
                                                {getInitials(
                                                    `${item.firstName || ""} ${item.lastName || ""}`
                                                )}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600}>
                                                {`${item.firstName || ""} ${item.lastName || ""}`}                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    {/* Email */}
                                    <TableCell sx={{ fontSize: "13px", color: "text.secondary" }}>
                                        {item.email || "—"}
                                    </TableCell>

                                    {/* Phone */}
                                    <TableCell sx={{ fontSize: "13px" }}>
                                        {item.phoneNumber || "—"}
                                    </TableCell>

                                    {/* Education */}
                                    <TableCell>
                                        {item.education ? (
                                            <Chip
                                                label={item.education}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "11px",
                                                    background: "rgba(46,125,50,0.08)",
                                                    color: "#2e7d32",
                                                }}
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>

                                    {/* Date */}
                                    <TableCell sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                                        {item.appliedAt
                                            ? new Date(item.appliedAt).toLocaleDateString()
                                            : "—"}
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                        <Tooltip title="View details">
                                            <IconButton
                                                size="small"
                                                onClick={() => setViewItem(item)}
                                                sx={{ color: "#2e7d32", mr: 0.5 }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteDialog({ open: true, id: item.id })}
                                                sx={{ color: "error.main" }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── View Dialog ──────────────────────────────────────────────────── */}
            <Dialog
                open={Boolean(viewItem)}
                onClose={() => setViewItem(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                            sx={{
                                width: 42, height: 42,
                                fontSize: "14px", fontWeight: 700,
                                background: avatarColor(viewItem?.name),
                            }}
                        >
                            {getInitials(viewItem?.name)}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>{viewItem?.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Career Application
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: "8px !important" }}>
                    {viewItem && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                            {/* Info chips row */}
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Chip
                                    icon={<EmailIcon sx={{ fontSize: "14px !important" }} />}
                                    label={viewItem.email || "—"}
                                    size="small"
                                    sx={{ background: "rgba(25,118,210,0.08)", color: "#1976d2", fontWeight: 600, fontSize: "11px" }}
                                />
                                {viewItem.phone && (
                                    <Chip
                                        icon={<PhoneIcon sx={{ fontSize: "14px !important" }} />}
                                        label={viewItem.phone}
                                        size="small"
                                        sx={{ background: "rgba(2,136,209,0.08)", color: "#0288d1", fontWeight: 600, fontSize: "11px" }}
                                    />
                                )}
                                {viewItem.position && (
                                    <Chip
                                        icon={<WorkOutlineIcon sx={{ fontSize: "14px !important" }} />}
                                        label={viewItem.position}
                                        size="small"
                                        sx={{ background: "rgba(46,125,50,0.08)", color: "#2e7d32", fontWeight: 600, fontSize: "11px" }}
                                    />
                                )}
                                {viewItem.createdAt && (
                                    <Chip
                                        icon={<CalendarTodayIcon sx={{ fontSize: "14px !important" }} />}
                                        label={new Date(viewItem.createdAt).toLocaleDateString()}
                                        size="small"
                                        sx={{ background: "rgba(0,0,0,0.05)", color: "text.secondary", fontWeight: 600, fontSize: "11px" }}
                                    />
                                )}
                            </Box>

                            {/* Detail fields */}
                            {[
                                ["Experience", viewItem.experience || viewItem.yearsOfExperience || "—"],
                                ["Qualification", viewItem.qualification || viewItem.education || "—"],
                                ["Location", viewItem.location || viewItem.address || "—"],
                                ["Portfolio / LinkedIn", viewItem.portfolio || viewItem.linkedIn || viewItem.portfolioUrl || "—"],
                            ].map(([label, value]) => (
                                value !== "—" && (
                                    <Box key={label}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.25 }}>{value}</Typography>
                                    </Box>
                                )
                            ))}

                            {/* Cover letter / message */}
                            {(viewItem.coverLetter || viewItem.message || viewItem.description) && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Cover Letter / Message
                                    </Typography>
                                    <Paper
                                        elevation={0}
                                        sx={{ mt: 0.5, p: 2, background: "rgba(0,0,0,0.03)", borderRadius: 2 }}
                                    >
                                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                            {viewItem.coverLetter || viewItem.message || viewItem.description}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}

                            {/* Resume link */}
                            {(viewItem.resumeUrl || viewItem.resume) && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Resume
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            href={viewItem.resumeUrl || viewItem.resume}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                textTransform: "none", borderRadius: 2,
                                                fontSize: "12px", fontWeight: 600,
                                            }}
                                        >
                                            View Resume →
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                            setViewItem(null);
                            setDeleteDialog({ open: true, id: viewItem?.id });
                        }}
                        sx={{ textTransform: "none", borderRadius: 2, mr: "auto" }}
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={() => setViewItem(null)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null })}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Delete Application?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This action cannot be undone. The application will be permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, id: null })}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminCareers;
